import { useCallback, useEffect, useRef, useState } from 'react';
import type { ChatImage } from '@engineer-os/chat';
import { ImageRefused, uploadImage, type ImageRefusal } from '../lib/image';

/**
 * The picture that has been picked and not yet sent.
 *
 * It is a hook rather than three `useState` calls in `ChatDock` for one
 * reason, and it is the object URL. A preview is a handle on bytes the browser
 * is holding for as long as the page asks it to, and every path that replaces
 * or clears an attachment has to release the previous one — picking a second
 * picture, cancelling, sending, switching rooms, closing the panel, unmounting
 * the tab. Six callers each remembering to revoke is six chances to leak a few
 * megabytes into a tab that is also running an emulator, and the one that
 * forgets is invisible. Here there is exactly one place that lets a preview
 * go, and it is the same place that makes one.
 *
 * ## Where it lives, and why not in the composer
 *
 * `ChatDock` holds it, beside the draft, and the argument is the one already
 * written beside the draft: a component that owns a rescue value and then
 * unmounts has lost the thing it was rescuing. The composer unmounts whenever
 * a listener arrives, a room is switched, or a dialog takes the panel over,
 * and a learner who picked a photograph and then opened somebody's profile
 * card should come back to the photograph.
 *
 * ## The three states, and why "failed" is one of them
 *
 * A picture is being prepared and uploaded, or it is ready, or it will not be
 * sent. The third could have been a toast and a silent clear, and that is the
 * version this is not: the thumbnail stays on screen with the reason on it,
 * because the learner is looking at the thing that failed and taking it away
 * from them is how you get somebody dragging the same file in four times. The
 * preview is a local object URL and does not depend on the upload having
 * worked, so there is always something to show.
 */
export interface Attachment {
  /** A local object URL for what was picked. Always present, even on failure. */
  preview: string;
  /** What the message will carry, once the bucket has it. Null while in flight. */
  image: ChatImage | null;
  /** Why it will not be sent, or null. */
  failed: ImageRefusal | null;
}

export interface AttachmentApi {
  attachment: Attachment | null;
  /** Prepare and upload one file, replacing whatever was there. */
  pick(file: Blob): void;
  /** Let it go, and release its preview. */
  clear(): void;
  /** True while something is on its way up: the send button waits for this. */
  busy: boolean;
}

export function useAttachment(): AttachmentApi {
  const [attachment, setAttachment] = useState<Attachment | null>(null);
  /**
   * The preview currently on screen, readable from a callback that may run
   * after several renders.
   *
   * State alone is not enough here: `pick` has to revoke the URL it is
   * replacing, and by the time an upload settles the state it closed over may
   * be two pictures out of date. A ref is what the cleanup reads.
   */
  const live = useRef<string | null>(null);
  /**
   * Which upload is the current one.
   *
   * A learner who drops a second picture while the first is still going gets
   * two promises in flight, and the first one settling afterwards would
   * overwrite the second's result with an older answer. Each attempt takes a
   * number and only the newest is allowed to write.
   */
  const attempt = useRef(0);

  const release = useCallback(() => {
    if (live.current) URL.revokeObjectURL(live.current);
    live.current = null;
  }, []);

  const clear = useCallback(() => {
    release();
    attempt.current += 1;
    setAttachment(null);
  }, [release]);

  const pick = useCallback(
    (file: Blob) => {
      release();
      const mine = (attempt.current += 1);
      const preview = URL.createObjectURL(file);
      live.current = preview;
      setAttachment({ preview, image: null, failed: null });

      void uploadImage(file)
        .then((image) => {
          if (attempt.current !== mine) return;
          setAttachment({ preview, image, failed: null });
        })
        .catch((err: unknown) => {
          if (attempt.current !== mine) return;
          // Anything that is not one of ours is a bug rather than a refusal,
          // and it is reported as the one reason that means "try again" —
          // which is the only useful thing to say about a fault nobody
          // anticipated.
          const reason = err instanceof ImageRefused ? err.reason : 'offline';
          setAttachment({ preview, image: null, failed: reason });
        });
    },
    [release],
  );

  // The tab going away is the one path that has no caller to do this.
  useEffect(() => release, [release]);

  return { attachment, pick, clear, busy: attachment !== null && attachment.image === null && attachment.failed === null };
}
